import { useAtom } from 'jotai';
import { useStore } from 'jotai/react';
import { useCallback, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { draftSelectionIdsAtom, isZoomingAtom, selectedIdsAtom, stageRefAtom, stageViewportAtom, } from '@/store/boardAtoms';
import { prioritizeCacheForVisibleElements } from '@/utils/board/imageCacheUtils';
import { endFastPan, startFastPan } from '@/utils/board/panZoomOptimization';
import { zoomWithCenter } from '@/utils/board/stageUtils';
/**
 * Кастомный хук для обработки событий колесика мыши (зум) на Stage
 *
 * @param params - Параметры хука
 * @returns Объект с обработчиками событий колесика мыши
 */
export const useStageWheelEvents = ({ selectedIds }) => {
    // Store для синхронного чтения атомов вне React-контекста
    const store = useStore();
    // Получаем setter функции из атома
    const [, setViewport] = useAtom(stageViewportAtom);
    const setZoom = useCallback((zoom) => {
        setViewport((prev) => {
            const newZoom = typeof zoom === 'function' ? zoom(prev.zoom) : zoom;
            return Object.assign(Object.assign({}, prev), { zoom: newZoom });
        });
    }, [setViewport]);
    const setPosition = useCallback((position) => {
        setViewport((prev) => {
            const newPosition = typeof position === 'function' ? position(prev.position) : position;
            return Object.assign(Object.assign({}, prev), { position: newPosition });
        });
    }, [setViewport]);
    const [, setIsZooming] = useAtom(isZoomingAtom);
    const [draftSelectionIds, setDraftSelectionIds] = useAtom(draftSelectionIdsAtom);
    const [, setSelectedIds] = useAtom(selectedIdsAtom);
    // Refs для throttling обновлений
    const lastUpdateTimeRef = useRef(0);
    const THROTTLE_MS = 16; // ~60fps
    const zoomStartCalledRef = useRef(false);
    /**
     * Debounced функция для возврата выделенных элементов после зума
     */
    const returnSelectedIdsAfterZoom = useDebouncedCallback(() => {
        setSelectedIds(draftSelectionIds);
    }, 500);
    /**
     * Debounced функция для завершения зума
     */
    const debouncedZoomEnd = useDebouncedCallback(() => {
        setIsZooming(false);
        zoomStartCalledRef.current = false;
        if (draftSelectionIds.length) {
            returnSelectedIdsAfterZoom();
        }
    }, 150);
    /**
     * Обработчик начала зума - вызывается только один раз в начале зума
     */
    const handleZoomStart = useCallback(() => {
        if (zoomStartCalledRef.current)
            return;
        zoomStartCalledRef.current = true;
        setIsZooming(true);
        if (selectedIds.length > 100) {
            setDraftSelectionIds(selectedIds);
            setSelectedIds([]);
        }
        // Приоритизируем кэширование видимых изображений для предотвращения фризов
        const stage = store.get(stageRefAtom);
        if (stage) {
            const currentViewport = store.get(stageViewportAtom);
            prioritizeCacheForVisibleElements(stage, currentViewport.zoom, currentViewport.size.width, currentViewport.size.height);
        }
    }, [selectedIds, setDraftSelectionIds, setSelectedIds, setIsZooming, store]);
    /**
     * Обработчик окончания зума
     */
    const handleZoomEnd = useCallback(() => {
        debouncedZoomEnd();
    }, [debouncedZoomEnd]);
    /**
     * Обработчик колесика мыши — zoom и перемещение сцены
     *
     * @param e - Событие колесика мыши
     */
    const onWheel = useCallback((e) => {
        e.evt.preventDefault();
        const stage = store.get(stageRefAtom);
        if (!stage)
            return;
        const pointer = stage.getPointerPosition();
        if (!pointer)
            return;
        // Throttling: обновляем максимум раз в 16ms (~60fps)
        const now = performance.now();
        if (now - lastUpdateTimeRef.current < THROTTLE_MS) {
            return;
        }
        lastUpdateTimeRef.current = now;
        // Определяем направление зума
        let direction = e.evt.deltaY > 0 ? -1 : 1;
        // При зуме на трекпаде с Ctrl, меняем направление
        if (e.evt.ctrlKey) {
            direction = -direction;
        }
        const SCALE_BY = 1.1;
        const zoomFactor = direction > 0 ? SCALE_BY : 1 / SCALE_BY;
        // Читаем актуальные значения из ref (без ререндера)
        const currentViewport = store.get(stageViewportAtom);
        const { newZoom, newPosition } = zoomWithCenter(currentViewport.zoom, currentViewport.position, pointer, zoomFactor);
        // Оптимизируем pan/zoom операцию
        startFastPan(stage);
        handleZoomStart();
        setZoom(newZoom);
        setPosition(newPosition);
        handleZoomEnd();
        endFastPan();
    }, [store, handleZoomStart, handleZoomEnd, setZoom, setPosition]);
    /**
     * Функция для зума внутрь
     */
    const zoomIn = useCallback(() => {
        const stage = store.get(stageRefAtom);
        if (!stage)
            return;
        const centerPoint = {
            x: window.innerWidth / 2,
            y: window.innerHeight / 2,
        };
        const currentViewport = store.get(stageViewportAtom);
        const { newZoom, newPosition } = zoomWithCenter(currentViewport.zoom, currentViewport.position, centerPoint, 1.1);
        // Оптимизируем pan/zoom операцию
        startFastPan(stage);
        handleZoomStart();
        setZoom(newZoom);
        handleZoomEnd();
        setPosition(newPosition);
        endFastPan();
    }, [store, handleZoomStart, handleZoomEnd, setZoom, setPosition]);
    /**
     * Функция для зума наружу
     */
    const zoomOut = useCallback(() => {
        const stage = store.get(stageRefAtom);
        if (!stage)
            return;
        const centerPoint = {
            x: window.innerWidth / 2,
            y: window.innerHeight / 2,
        };
        const currentViewport = store.get(stageViewportAtom);
        const { newZoom, newPosition } = zoomWithCenter(currentViewport.zoom, currentViewport.position, centerPoint, 1 / 1.1);
        // Оптимизируем pan/zoom операцию
        startFastPan(stage);
        handleZoomStart();
        setZoom(newZoom);
        handleZoomEnd();
        setPosition(newPosition);
        endFastPan();
    }, [store, handleZoomStart, handleZoomEnd, setZoom, setPosition]);
    /**
     * Функция для сброса зума в 100%
     */
    const zoomReset = useCallback(() => {
        const currentViewport = store.get(stageViewportAtom);
        if (currentViewport.zoom === 1)
            return;
        const stage = store.get(stageRefAtom);
        if (!stage)
            return;
        const centerPoint = {
            x: window.innerWidth / 2,
            y: window.innerHeight / 2,
        };
        const targetZoom = 1;
        const zoomFactor = targetZoom / currentViewport.zoom;
        const { newZoom, newPosition } = zoomWithCenter(currentViewport.zoom, currentViewport.position, centerPoint, zoomFactor);
        // Оптимизируем pan/zoom операцию
        startFastPan(stage);
        handleZoomStart();
        setZoom(newZoom);
        handleZoomEnd();
        setPosition(newPosition);
        endFastPan();
    }, [store, handleZoomStart, handleZoomEnd, setZoom, setPosition]);
    return {
        onWheel,
        zoomIn,
        zoomOut,
        zoomReset,
        handleZoomStart,
        handleZoomEnd,
    };
};
